import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import {
  IonApp,
  IonContent,
  IonFooter,
  IonMenu,
  IonRouterOutlet,
  IonSplitPane,
  ToastController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ConnectivityNoticeService,
  OfflineOverlayComponent,
  ScreensizeService,
  TOP_BAR_NOTICES,
} from '@okcargo-apps/layout-ionic';
import {
  AccountPreferencesCarrierService,
  AccountService,
  SessionLifecycleService,
} from '@okcargo-apps/shared-application-ionic';
import { CheckVersionService, ConnectivityService, PwaUpdateService } from '@okcargo-apps/shared-infrastructure';
import {
  AnalyticsService,
  FirebaseService,
  PlatformService,
  PushNotificationService,
} from '@okcargo-apps/shared-infrastructure-ionic';
import { RefreshDataService } from './common/application/refresh-data.service';
import { MenuFooterComponent } from './layout/components/menu-footer/menu-footer.component';
import { MenuHeaderComponent } from './layout/components/menu-header/menu-header.component';
import { MenuMainComponent } from './layout/components/menu-main/menu-main.component';
import { TopBarNoticesService } from './notice/application/top-bar-notices.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [
    CommonModule,
    IonApp,
    IonRouterOutlet,
    IonFooter,
    IonMenu,
    IonSplitPane,
    IonContent,
    TranslateModule,
    MenuHeaderComponent,
    MenuMainComponent,
    MenuFooterComponent,
    OfflineOverlayComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  // `ScreensizeService` is provided at the root injector (it is also used from Ionic modals, which
  // do not inherit the providers declared here). Only the notices token is app-specific.
  providers: [{ provide: TOP_BAR_NOTICES, useExisting: TopBarNoticesService }],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);
  private readonly checkVersionService = inject(CheckVersionService);
  private readonly pwaUpdateService = inject(PwaUpdateService);
  private readonly accountService = inject(AccountService);
  private readonly platformService = inject(PlatformService);
  private readonly connectivityService = inject(ConnectivityService);
  private readonly connectivityNoticeService = inject(ConnectivityNoticeService);

  /**
   * While the blocking layer is visible (browser without connectivity) the app content is inert, so
   * it cannot be reached with the keyboard either. The layer and the toast are rendered outside it.
   */
  protected readonly blocksInteraction = this.connectivityNoticeService.isOverlayVisible;
  private readonly toastCtrl = inject(ToastController);
  private readonly screensizeService = inject(ScreensizeService);
  private readonly refreshDataService = inject(RefreshDataService);
  private readonly sessionLifecycle = inject(SessionLifecycleService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly pushNotificationService = inject(PushNotificationService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly translate = inject(TranslateService);

  // TODO review this
  private readonly _initPreferences = inject(AccountPreferencesCarrierService);

  protected isAuthenticated = this.accountService.isAuthenticated;

  public readonly isUpdateRequired = this.checkVersionService.isUpdateRequired;

  /**
   * Whether the version check blocks the app. Without connectivity the check is skipped, so the app
   * renders instead of waiting for a result that cannot arrive; with connectivity the previous
   * behaviour is kept (placeholder while checking and the mandatory update screen when required).
   */
  protected readonly isVersionCheckBlocking = computed(
    () => this.checkVersionService.isUpdateRequired() === undefined && this.connectivityService.isOnline(),
  );

  public menuPosition = computed(() => {
    return this.screensizeService.isDesktopView() ? 'start' : 'end';
  });

  constructor() {
    const browserLang = navigator.language || navigator.languages[0];
    const defaultLang = browserLang.startsWith('es') ? 'es' : 'en';
    this.translate.setFallbackLang(defaultLang);
    this.translate.use(defaultLang);

    effect(() => {
      const account = this.accountService.account();
      if (account && this.translate.getFallbackLang() !== account.lang) {
        this.translate.setFallbackLang(account.lang);
        this.translate.use(account.lang);
      }
    });

    effect(() => {
      if (this.isUpdateRequired()) {
        this.router.navigate(['/update-required'], { replaceUrl: true });
      }
    });

    this.intializeDeepLinks();
  }

  // TODO configure background color for EdgeToEdge (Android 15) and dark mode
  // see https://www.npmjs.com/package/@capawesome/capacitor-android-edge-to-edge-support

  async ngOnInit(): Promise<void> {
    await this.platformService.ready();
    // The connectivity status is started by the app bootstrap (provideConnectivityBootstrap), so it is
    // already known here.
    this.connectivityNoticeService.start();

    if (!this.platformService.isHybrid) {
      effect(
        () => {
          if (this.pwaUpdateService.isUpdateReady()) {
            this.handlePwaUpdate();
          }
        },
        { injector: this.injector },
      );
    }

    await this.firebaseService.initialize();

    this.analyticsService.trackPageViews();

    this.sessionLifecycle.start({
      refreshAll: () => this.refreshDataService.refreshAll(),
      refreshPendingData: () => this.refreshDataService.refreshPendingData(),
      // Carriers refresh their data when the session is verified and register the push token.
      onSessionVerified: () => this.refreshDataService.refreshAll(),
      registerPushToken: () => this.pushNotificationService.registerFcmToken(),
    });
  }

  ngOnDestroy(): void {
    this.sessionLifecycle.stop();
  }

  public reloadApp(): void {
    window.location.href = window.location.origin;
  }

  private async handlePwaUpdate(): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: this.translate.instant('general.update_version_available'),
      color: 'danger',
      position: 'bottom',
      buttons: [
        {
          text: this.translate.instant('general.update'),
          role: 'info',
          handler: () => {
            this.applyUpdate();
          },
        },
      ],
    });
    await toast.present();
  }

  private async applyUpdate(): Promise<void> {
    await this.pwaUpdateService.activateUpdate();
    this.reloadApp();
  }

  // TODO if we want more functionality (e.g. adding metrics, different behavior), move to the standalone service
  private intializeDeepLinks(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const slug = event.url.split('.io').pop();
        if (slug) {
          this.router.navigateByUrl(slug);
        }
      });
    });
  }
}
